const OVER_TEMPLATE_IDS = new Set(['o', 'ov', 'over']);
const UNDER_TEMPLATE_IDS = new Set(['u', 'un', 'under']);

const LINE_SUFFIX = '(?:\\s+[+-]?\\d+(?:\\.\\d+)?)?';
const LINE_PREFIX = '(?:[+-]?\\d+(?:\\.\\d+)?\\s+)?';

/** Weight for classic Over/Under labels only — not combo names like "Home and under". */
export const getOverUnderColumnWeight = (columnName: string): number => {
    const name = columnName.toLowerCase().trim();

    if (OVER_TEMPLATE_IDS.has(name)) {
        return 0;
    }
    if (UNDER_TEMPLATE_IDS.has(name)) {
        return 1;
    }

    // "Over", "Over 1.5", "4.5 Over"
    if (new RegExp(`^${LINE_PREFIX}over${LINE_SUFFIX}$`).test(name)) {
        return 0;
    }
    // "Under", "Under 1.5", "4.5 Under"
    if (new RegExp(`^${LINE_PREFIX}under${LINE_SUFFIX}$`).test(name)) {
        return 1;
    }

    return 2;
};

/** Stable sort: Over columns first, then Under, then everything else in original order. */
export const sortOverUnderColumnNames = (columnNames: Array<string>): Array<string> => {
    return columnNames
        .map((name, index) => ({ name, index }))
        .sort((a, b) => {
            const weightDiff = getOverUnderColumnWeight(a.name) - getOverUnderColumnWeight(b.name);
            if (weightDiff !== 0) {
                return weightDiff;
            }
            return a.index - b.index;
        })
        .map(({ name }) => name);
};

type OverUnderGroupEntry<T> = [string, T[]];

const groupEntryWeight = <T extends { name: string; templateId?: string; identifier?: string }>(
    key: string,
    group: T[]
): number => {
    const fromKey = getOverUnderColumnWeight(key);
    if (fromKey < 2) {
        return fromKey;
    }

    const first = group[0];
    if (first === undefined) {
        return 2;
    }

    const fromTemplate = getOverUnderColumnWeight(first.identifier ?? first.templateId ?? '');
    if (fromTemplate < 2) {
        return fromTemplate;
    }

    return getOverUnderColumnWeight(first.name);
};

const selectionWeight = <T extends { name: string; templateId?: string; identifier?: string }>(
    selection: T
): number => {
    const fromTemplate = getOverUnderColumnWeight(selection.identifier ?? selection.templateId ?? '');
    if (fromTemplate < 2) {
        return fromTemplate;
    }
    return getOverUnderColumnWeight(selection.name);
};

const isPureOverUnderWeightList = (weights: Array<number>): boolean => {
    return weights.every((weight) => weight < 2) && weights.includes(0) && weights.includes(1);
};

/**
 * Stable sort of selection groups so Over is before Under.
 * Only reorders when every group is a pure Over/Under column (avoids combo markets).
 */
export const sortOverUnderSelectionGroups = <T extends { name: string; templateId?: string; identifier?: string }>(
    entries: Array<OverUnderGroupEntry<T>>
): Array<OverUnderGroupEntry<T>> => {
    if (entries.length < 2) {
        return entries;
    }

    const weights = entries.map(([key, group]) => groupEntryWeight(key, group));
    if (!isPureOverUnderWeightList(weights)) {
        return entries;
    }

    return entries
        .map((entry, index) => ({ entry, index, weight: weights[index] ?? 2 }))
        .sort((a, b) => {
            if (a.weight !== b.weight) {
                return a.weight - b.weight;
            }
            return a.index - b.index;
        })
        .map(({ entry }) => entry);
};

/** Stable sort within a shared group (e.g. display-template single). */
export const sortOverUnderSelections = <T extends { name: string; templateId?: string; identifier?: string }>(
    selections: Array<T>
): Array<T> => {
    if (selections.length < 2) {
        return selections;
    }

    const weights = selections.map((selection) => selectionWeight(selection));
    if (!isPureOverUnderWeightList(weights)) {
        return selections;
    }

    return selections
        .map((selection, index) => ({ selection, index, weight: weights[index] ?? 2 }))
        .sort((a, b) => {
            if (a.weight !== b.weight) {
                return a.weight - b.weight;
            }
            return a.index - b.index;
        })
        .map(({ selection }) => selection);
};
